import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import api, { formatApiError } from "../lib/api";

// Bulk-create-RP from selected reserved pallets.
// Architecture preserved: still calls POST /api/orders/{id}/rp with
// `link_reserved_pallet_ids` so RP remains the canonical production event
// (audit, KPI, history) — we just save the operator from re-entering data.
export default function BulkCreateRpModal({
    orderId,
    selectedPallets,
    onClose,
    onCreated,
}) {
    const todayIso = new Date().toISOString().slice(0, 10);
    const [productionDate, setProductionDate] = useState(todayIso);
    const [notes, setNotes] = useState("");
    const [rows, setRows] = useState(
        selectedPallets.map((p) => ({
            id: p.id,
            pallet_number: p.pallet_number,
            order_line_id: p.order_line_id,
            product_name_snapshot: p.product_name_snapshot,
            units_per_pallet: p.units_per_pallet || "",
        })),
    );
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const lineIds = useMemo(
        () => Array.from(new Set(rows.map((r) => r.order_line_id))),
        [rows],
    );
    const singleLine = lineIds.length === 1;

    const totalUnits = useMemo(
        () => rows.reduce((s, r) => s + (Number(r.units_per_pallet) || 0), 0),
        [rows],
    );

    const updateRow = (id, value) =>
        setRows((rs) =>
            rs.map((r) =>
                r.id === id ? { ...r, units_per_pallet: value } : r,
            ),
        );

    const submit = async () => {
        setError("");
        if (!singleLine) {
            setError(
                "All selected pallets must belong to the SAME order line to share one RP. Deselect pallets from other lines and try again.",
            );
            return;
        }
        for (const r of rows) {
            const n = Number(r.units_per_pallet);
            if (!Number.isFinite(n) || n <= 0) {
                setError(
                    `Pallet ${r.pallet_number} needs a positive unit count before creating the RP.`,
                );
                return;
            }
        }

        // Group pallets by units_per_pallet so the existing RP API contract
        // (multiple pallet_groups) keeps working unchanged.
        const groupsMap = new Map();
        const orderedPalletIds = [];
        for (const r of rows) {
            const upp = Math.floor(Number(r.units_per_pallet));
            const cur = groupsMap.get(upp) || { pallets: 0, ids: [] };
            cur.pallets += 1;
            cur.ids.push(r.id);
            groupsMap.set(upp, cur);
        }
        const palletGroups = [];
        // Preserve a deterministic ID order so reserved-queue consumption is
        // predictable (each group consumes its own pallets in row order).
        for (const [upp, grp] of groupsMap.entries()) {
            palletGroups.push({ pallets: grp.pallets, units_per_pallet: upp });
            orderedPalletIds.push(...grp.ids);
        }

        setBusy(true);
        try {
            const { data: rp } = await api.post(`/orders/${orderId}/rp`, {
                production_date: productionDate,
                order_line_id: lineIds[0],
                pallet_groups: palletGroups,
                link_reserved_pallet_ids: orderedPalletIds,
                notes: notes || undefined,
            });
            onCreated?.(rp);
            onClose?.();
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            isOpen
            onClose={onClose}
            title="Create RP From Selected Pallets"
            data-testid="bulk-rp-modal"
        >
            <div className="space-y-4" data-testid="bulk-rp-content">
                <p className="text-sm text-gray-600">
                    The selected pallets will be consumed into a single Record
                    Production. RP remains the official production event for
                    audit + KPI; this just saves you from re-entering each
                    pallet manually.
                </p>

                {!singleLine && (
                    <div
                        className="text-xs border border-amber-300 bg-amber-50 text-amber-800 rounded-sm px-3 py-2"
                        data-testid="bulk-rp-multi-line-warning"
                    >
                        Selection spans <b>{lineIds.length}</b> different order
                        lines. RPs are per-line — please pick pallets from one
                        line at a time.
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Production date
                        </label>
                        <input
                            type="date"
                            value={productionDate}
                            onChange={(e) => setProductionDate(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm odr-mono"
                            data-testid="bulk-rp-date"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Notes (optional)
                        </label>
                        <input
                            type="text"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Operator notes…"
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                            data-testid="bulk-rp-notes"
                        />
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="odr-table" data-testid="bulk-rp-pallet-table">
                        <thead>
                            <tr>
                                <th>PLL</th>
                                <th>Product</th>
                                <th className="text-right">Units</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} data-testid={`bulk-rp-row-${r.pallet_number}`}>
                                    <td className="odr-mono text-xs font-semibold">
                                        {r.pallet_number}
                                    </td>
                                    <td className="text-xs">
                                        {r.product_name_snapshot || "—"}
                                    </td>
                                    <td className="text-right">
                                        <input
                                            type="number"
                                            min="1"
                                            value={r.units_per_pallet}
                                            onChange={(e) =>
                                                updateRow(r.id, e.target.value)
                                            }
                                            placeholder="qty"
                                            className="odr-input w-28 rounded-sm border border-gray-300 px-2 text-xs odr-mono text-right"
                                            data-testid={`bulk-rp-units-${r.pallet_number}`}
                                        />
                                    </td>
                                </tr>
                            ))}
                            <tr className="font-semibold bg-gray-50">
                                <td colSpan={2} className="text-xs uppercase tracking-wider">
                                    Total — {rows.length} pallet
                                    {rows.length === 1 ? "" : "s"}
                                </td>
                                <td className="text-right odr-mono text-sm">
                                    {totalUnits.toLocaleString()}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {error && (
                    <div
                        className="text-xs border border-red-300 bg-red-50 text-red-800 rounded-sm px-3 py-2"
                        data-testid="bulk-rp-error"
                    >
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
                        data-testid="bulk-rp-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy || !singleLine}
                        className="px-4 py-2 text-sm font-semibold bg-red-700 text-white rounded-sm hover:bg-red-800 disabled:bg-gray-300"
                        data-testid="bulk-rp-submit"
                    >
                        {busy ? "Creating RP…" : "Create RP"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
